<script setup lang="ts">
import { computed, inject, ref, toRef } from "vue";
import { uid } from "@grozav/utils";
import { FormKey, FormGroupKey } from "@inkline/inkline/constants";
import {
  useComponentColor,
  useComponentSize,
  useFormValidationError,
  useValidation
} from "@inkline/inkline/composables";

const componentName = 'IToggle'

interface Props {
  color?: string,
  disabled?: boolean,
  error?: Array<string> | boolean,
  indeterminate?: boolean,
  value?: boolean,
  modelValue?: boolean,
  id?: string,
  name?: string,
  native?: boolean,
  readonly?: boolean,
  size?: 'sm' | 'md' | 'lg',
  tabindex?: string | number,
  validate?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  error: () => ["touched", "dirty", "invalid"],
  indeterminate: false,
  value: false,
  modelValue: false,
  tabindex: 0,
  validate: true,
  name: uid("toggle")
})

const emit = defineEmits<{
  'update:modelValue': [e: boolean | undefined]
}>()

const inputRef = ref<HTMLInputElement>();

const form = inject(FormKey, null);
const formGroup = inject(FormGroupKey, null);

const currentColor = computed(
  () => props.color || formGroup?.color.value || form?.color.value
);

const currentSize = computed(() => props.size || formGroup?.size.value || form?.size.value);
const { color } = useComponentColor({ componentName, currentColor });
const { size } = useComponentSize({ componentName, currentSize });
const disabled = computed(
  () => props.disabled || formGroup?.disabled.value || form?.disabled.value
);
const readonly = computed(
  () => props.readonly || formGroup?.readonly.value || form?.readonly.value
);
const name = toRef(props, "name");
const validate = toRef(props, "validate");

const { schema, onInput: schemaOnInput, onBlur: schemaOnBlur } = useValidation({
  name,
  validate
});

const error = toRef(props, "error");

const { hasError } = useFormValidationError({
  schema,
  error
});

const classes = computed(() => ({
  [`-${color.value}`]: true,
  [`-${size.value}`]: true,
  "-disabled": disabled.value,
  "-readonly": readonly.value,
  "-native": props.native,
  "-error": hasError.value
}));

const checked = computed(() => {
  if (schema.value) {
    return Boolean(schema.value.value);
  }
  return props.modelValue;
});

const tabindex = computed(() => {
  return disabled.value ? -1 : props.tabindex;
});

function onChange(event: Event) {
  const target = <HTMLInputElement>event.target;
  schemaOnInput(name, target.checked);
  emit("update:modelValue", target.checked);
}
</script>

<template>
  <div v-bind="$attrs" class="toggle" :class="classes" :aria-checked="checked" :aria-disabled="disabled"
    :aria-readonly="readonly" role="checkbox">
    <input :id="id" ref="inputRef" type="checkbox" :checked="checked" :disabled="disabled" :readonly="readonly"
      :aria-checked="checked" :aria-disabled="disabled" :aria-readonly="readonly" :name="name" @change="onChange" />
    <label class="toggle-label" :for="id" :tabindex="tabindex">
      <!-- @slot default Slot for default toggle label -->
      <slot />
    </label>
  </div>
</template>
